import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Form, Image, Modal, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import {
  CustomDropdownContainer,
  FormFieldLabel,
  PageHeader,
} from "CommonComponent";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  deleteUserManual,
  fetchUserManualFileBlob,
  fetchUserManualOptions,
  fetchUserManuals,
  formatFileSize,
  getErrorMessage,
  isAllowedPdfSize,
  isPdfFile,
  saveUserManual,
} from "./userManualApi";
import "./UserManual.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const selectMenuProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 10050 }),
  },
};

const emptyForm = () => ({
  title: "",
  file: null,
  role_ids: [],
});

const UserManual = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [rows, setRows] = useState([]);
  const [capabilities, setCapabilities] = useState({
    can_upload: false,
    can_replace: false,
    can_delete: false,
    can_manage: false,
  });
  const [roleOptions, setRoleOptions] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const load = useCallback(async () => {
    if (!objectViewedId) return;
    dispatch(showCommonLoader());
    try {
      const result = await fetchUserManuals(objectViewedId);
      setRows(result.data);
      setCapabilities(result.capabilities);
      if (result.capabilities.can_manage) {
        const options = await fetchUserManualOptions(objectViewedId);
        setRoleOptions(options.roles || []);
      }
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  }, [dispatch, objectViewedId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectOptions = useMemo(
    () => roleOptions.map((role) => ({ value: role.id, label: role.name })),
    [roleOptions]
  );

  const closeForm = (force = false) => {
    if (uploading && !force) return;
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
    setUploading(false);
    setUploadProgress(0);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      title: row.title || "",
      file: null,
      role_ids: Array.isArray(row.role_ids) ? row.role_ids : [],
    });
    setShowForm(true);
  };

  const validateForm = () => {
    if (!form.title.trim()) {
      dispatch(notify("Manual title is required."));
      return false;
    }
    if (!editingId && !form.file) {
      dispatch(notify("Please upload a PDF file."));
      return false;
    }
    if (form.file && !isPdfFile(form.file)) {
      dispatch(notify("Only PDF files are allowed."));
      return false;
    }
    if (form.file && !isAllowedPdfSize(form.file)) {
      dispatch(notify("The PDF may not be larger than 10 MB."));
      return false;
    }
    return true;
  };

  const save = async () => {
    if (uploading || !validateForm()) return;
    const hasFile = Boolean(form.file);
    const snapshot = {
      title: form.title.trim(),
      file: form.file,
      role_ids: Array.isArray(form.role_ids) ? [...form.role_ids] : [],
    };
    if (hasFile) {
      setUploading(true);
      setUploadProgress(0);
    } else {
      dispatch(showCommonLoader());
    }
    try {
      await saveUserManual(
        snapshot,
        objectViewedId,
        editingId,
        hasFile ? setUploadProgress : null
      );
      dispatch(
        notify(editingId ? "User manual updated." : "User manual uploaded.", "success")
      );
      closeForm(true);
      await load();
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      if (hasFile) {
        setUploading(false);
        setUploadProgress(0);
      } else {
        dispatch(hideCommonLoader());
      }
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    dispatch(showCommonLoader());
    try {
      await deleteUserManual(deleteId, objectViewedId);
      dispatch(notify("User manual deleted.", "success"));
      setDeleteId(null);
      await load();
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  };

  const openPdf = async (row) => {
    dispatch(showCommonLoader());
    try {
      const blob = await fetchUserManualFileBlob(row.id, objectViewedId);
      const pdfBlob =
        blob instanceof Blob
          ? blob.type
            ? blob
            : new Blob([blob], { type: "application/pdf" })
          : new Blob([blob], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  };

  const roleLabel = (row) => {
    const names = (row.roles || []).map((role) => role.name).filter(Boolean);
    if (names.length === 0) return "All users with access";
    return names.join(", ");
  };

  return (
    <div className="user-manual-page">
      <PageHeader title="User Manual" />

      <div className="main-content-div page-card">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <p className="user-manual-hint mb-0">
            View and download the user manuals available for your access.
          </p>
          {capabilities.can_upload ? (
            <button type="button" className="n-u-btn" onClick={openCreate}>
              Upload PDF
            </button>
          ) : null}
        </div>

        {rows.length === 0 ? (
          <div className="user-manual-empty">No user manual is available.</div>
        ) : (
          <Table responsive>
            <thead>
              <tr>
                <th>Title</th>
                <th>File</th>
                <th>Assigned access</th>
                <th className="form-action-width text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.title || "—"}</td>
                  <td>
                    {row.original_file_name || "PDF"}
                    <div className="user-manual-role-list">
                      {formatFileSize(row.file_size)}
                    </div>
                  </td>
                  <td className="user-manual-role-list">{roleLabel(row)}</td>
                  <td className="form-action-width text-end">
                    <div className="user-manual-actions">
                      <button
                        type="button"
                        className="c-b-btn user-manual-action-btn"
                        title="Open in a new tab"
                        onClick={() => openPdf(row)}
                      >
                        Open
                      </button>
                      {capabilities.can_replace ? (
                        <Image
                          src="/assets/images/edit-icon.svg"
                          width={20}
                          className="cursor-pointer"
                          title="Update"
                          alt="Update"
                          onClick={() => openEdit(row)}
                        />
                      ) : null}
                      {capabilities.can_delete ? (
                        <Image
                          src="/assets/images/delete-icon.svg"
                          width={20}
                          className="cursor-pointer"
                          title="Delete"
                          alt="Delete"
                          onClick={() => setDeleteId(row.id)}
                        />
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <Modal
        show={showForm}
        onHide={() => closeForm()}
        centered
        backdrop={uploading ? "static" : true}
        keyboard={!uploading}
      >
        <Modal.Header className="heading-color" closeButton>
          <Modal.Title className="font-20 font-weight-s-bold heading-color">
            {editingId ? "Update User Manual" : "Upload User Manual"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <FormFieldLabel>Title</FormFieldLabel>
              <Form.Control
                value={form.title}
                disabled={uploading}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="User manual title"
              />
            </Col>
            <Col md={12}>
              <FormFieldLabel>PDF file</FormFieldLabel>
              <Form.Control
                type="file"
                accept="application/pdf,.pdf"
                disabled={uploading}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))
                }
              />
              <div className="user-manual-hint mt-2 mb-0">
                {uploading
                  ? `Uploading PDF... ${uploadProgress}%`
                  : `PDF only, up to 10 MB.${editingId ? " Leave empty to keep the current file." : ""}`}
              </div>
            </Col>
            <Col md={12}>
              <FormFieldLabel>Assign to existing roles</FormFieldLabel>
              <Select
                isMulti
                isDisabled={uploading}
                className="multi-select"
                classNamePrefix="react-select"
                options={selectOptions}
                value={selectOptions.filter((option) =>
                  form.role_ids.includes(option.value)
                )}
                onChange={(selected) =>
                  setForm((prev) => ({
                    ...prev,
                    role_ids: (selected || []).map((option) => option.value),
                  }))
                }
                placeholder="All users with User Manual access"
                components={{ ValueContainer: CustomDropdownContainer }}
                {...selectMenuProps}
              />
              <div className="user-manual-hint mt-2 mb-0">
                Leave empty to make this manual available to everyone who can open User Manual.
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="c-b-btn"
            onClick={() => closeForm()}
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="n-u-btn"
            onClick={save}
            disabled={uploading}
          >
            {uploading
              ? `Uploading PDF... ${uploadProgress}%`
              : editingId
                ? "Save"
                : "Upload"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deleteId} onHide={() => setDeleteId(null)} centered>
        <Modal.Header className="heading-color" closeButton>
          <Modal.Title className="font-20 font-weight-s-bold heading-color">
            Delete User Manual
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>Delete this user manual? This cannot be undone.</Modal.Body>
        <Modal.Footer>
          <button type="button" className="c-b-btn" onClick={() => setDeleteId(null)}>
            Cancel
          </button>
          <button type="button" className="delete-btn" onClick={confirmDelete}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserManual;
